import { useState, useEffect } from 'react';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { isMobileBlurFeatureEnabled } from './featureFlags';
import {
  getDeviceCapabilities,
  getRecommendedBlurEngine,
  type BlurEngine,
  type DeviceCapabilities,
} from './deviceCapabilities';

export const useBlur = () => {
  const [isBlurEnabled, setIsBlurEnabled] = useState(() => {
    const storedValue = getWithExpiry('isBlurEnabled');
    if (typeof storedValue === 'boolean') return storedValue;
    if (typeof storedValue === 'string') return storedValue === 'true';
    return false;
  });

  const [isBlurSupported, setIsBlurSupported] = useState<boolean | null>(null);
  const [blurError, setBlurError] = useState<string | null>(null);
  const [deviceCapabilities, setDeviceCapabilities] = useState<DeviceCapabilities | null>(null);
  const [engine, setEngine] = useState<BlurEngine | null>(null);

  useEffect(() => {
    const checkBlurSupport = () => {
      try {
        console.log('🔍 Checking blur support...');

        const isMobileOrTablets = window.innerWidth < 831;
        if (isMobileOrTablets && !isMobileBlurFeatureEnabled()) {
          console.warn('📱 Mobile blur feature disabled by feature flag');
          setIsBlurSupported(false);
          return;
        }

        // QA testing overrides — preserved from legacy implementation
        const testScenario = localStorage.getItem('blurTestScenario');

        if (testScenario === 'legacy-browser') {
          console.warn('🧪 TESTING: Simulating legacy browser');
          setBlurError('Blur not supported on legacy browser');
          setIsBlurSupported(false);
          return;
        }

        if (testScenario === 'no-webgl') {
          console.warn('🧪 TESTING: Simulating no WebGL');
          setBlurError('Blur requires WebGL support');
          setIsBlurSupported(false);
          return;
        }

        if (testScenario === 'no-capturestream') {
          console.warn('🧪 TESTING: Simulating no captureStream');
          setBlurError('Blur requires Canvas captureStream support');
          setIsBlurSupported(false);
          return;
        }

        const capabilities = getDeviceCapabilities();
        setDeviceCapabilities(capabilities);
        console.log('📱 Device capabilities:', capabilities);

        if (capabilities.isLegacyBrowser) {
          const browserInfo = Object.entries(capabilities.browserInfo)
            .filter(([_, isOld]) => isOld)
            .map(([browser]) => browser)
            .join(', ');
          const errorMsg = `Blur not supported on legacy browser: ${browserInfo}`;
          console.warn('❌', errorMsg);
          setBlurError(errorMsg);
          setIsBlurSupported(false);
          return;
        }

        if (!capabilities.hasCaptureStream) {
          const errorMsg = 'Blur requires Canvas captureStream support';
          console.warn('❌', errorMsg);
          setBlurError(errorMsg);
          setIsBlurSupported(false);
          return;
        }

        if (!capabilities.hasMediaRecorder) {
          const errorMsg = 'Blur requires MediaRecorder support';
          console.warn('❌', errorMsg);
          setBlurError(errorMsg);
          setIsBlurSupported(false);
          return;
        }

        const selectedEngine = getRecommendedBlurEngine();
        if (selectedEngine === null) {
          const errorMsg = capabilities.isSafari && !capabilities.hasWebGL2
            ? 'Blur not supported on this Safari version (requires WebGL2)'
            : 'Blur not supported on this browser/device';
          console.warn('❌', errorMsg);
          setBlurError(errorMsg);
          setIsBlurSupported(false);
          return;
        }

        setEngine(selectedEngine);
        setIsBlurSupported(true);
        setBlurError(null);
        console.log(`✅ Blur supported with engine: ${selectedEngine}`);
      } catch (error: unknown) {
        const errorMsg = `Blur support check failed: ${error instanceof Error ? error.message : String(error)}`;
        console.warn('❌', errorMsg);
        setBlurError(errorMsg);
        setIsBlurSupported(false);
      }
    };

    checkBlurSupport();
  }, []);

  useEffect(() => {
    if (isBlurSupported === false && isBlurEnabled) {
      console.log('🔄 Disabling blur due to lack of support');
      setIsBlurEnabled(false);
    }
  }, [isBlurSupported, isBlurEnabled]);

  useEffect(() => {
    setWithExpiry('isBlurEnabled', isBlurEnabled, 24 * 60 * 60 * 1000);
    console.log('💾 Stored blur state:', isBlurEnabled);
  }, [isBlurEnabled]);

  const toggleBlur = () => {
    if (!isBlurSupported) {
      console.warn('⚠️ Cannot toggle blur - not supported on this device');
      return;
    }
    const newBlurState = !isBlurEnabled;
    console.log('🔄 Toggling blur from:', isBlurEnabled, 'to:', newBlurState);
    setIsBlurEnabled(newBlurState);
  };

  return {
    isBlurEnabled,
    isBlurSupported,
    blurError,
    deviceCapabilities,
    engine,
    toggleBlur,
    setIsBlurEnabled,
  };
};
